import { showAlert, showConfirm } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import { clearBiometricCredential } from '@/features/auth/services/biometric-credentials';
import { useBiometricStore } from '@/features/auth/stores/biometric-store';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { resetCurrentPassword, signOut } from '@/libs/firebase/auth';
import { RootStackParamList } from '@/navigations';
import { useSessionStore } from '@/stores/session-store';
import { useUiStore } from '@/stores/ui-store';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useCallback, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';

type FieldKey = 'currentPassword' | 'newPassword' | 'confirmPassword';

export function useChangePassword() {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const user = useSessionStore(state => state.user);
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(s => ({
      showGlobalLoading: s.showGlobalLoading,
      hideGlobalLoading: s.hideGlobalLoading,
    })),
  );

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmMismatchError, setConfirmMismatchError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({});

  const hasChanges = useMemo(
    () => currentPassword.length > 0 || newPassword.length > 0 || confirmPassword.length > 0,
    [confirmPassword.length, currentPassword.length, newPassword.length],
  );

  const handleCancel = useCallback(async () => {
    if (!hasChanges) {
      navigation.goBack();
      return;
    }
    const shouldLeave = await showConfirm({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.auth.changePassword.cancelConfirmMessage,
      cancelText: STRINGS.auth.changePassword.buttonCancel,
      confirmText: STRINGS.common.buttons.continue,
    });
    if (shouldLeave) navigation.goBack();
  }, [hasChanges, navigation]);

  const handleUpdatePassword = useCallback(async () => {
    const nextErrors: Partial<Record<FieldKey, string>> = {};
    // if (currentPassword.length < 8) {
    //   nextErrors.currentPassword = STRINGS.auth.changePassword.validationMinLength;
    // }
    if (newPassword.length < 8)
      nextErrors.newPassword = STRINGS.auth.changePassword.validationMinLength;
    if (confirmPassword.length < 8) {
      nextErrors.confirmPassword = STRINGS.auth.changePassword.validationMinLength;
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (newPassword !== confirmPassword) {
      setConfirmMismatchError(STRINGS.auth.changePassword.validationConfirmMismatch);
      return;
    }
    setConfirmMismatchError(null);

    const email = user?.email?.trim();
    if (!email) {
      showAlert({
        title: STRINGS.common.noticeTitle,
        message: STRINGS.auth.changePassword.errorSessionExpired,
        buttons: [{ text: STRINGS.common.buttons.dismiss }],
      });
      return;
    }

    try {
      showGlobalLoading();
      await resetCurrentPassword({
        email,
        currentPassword,
        newPassword,
      });

      // Security: the password just changed, so the stored biometric credential (tied to the OLD
      // password/token) is now stale. Invalidate it for THIS account — remove the Keychain secret
      // and the biometric-store binding — so biometric unlock is disconnected until the user signs
      // in with the new password and re-opts into biometrics.
      const uid = user?.uid;
      if (uid) {
        await clearBiometricCredential(uid);
        useBiometricStore.getState().disable(uid);
      }

      showAlert({
        title: STRINGS.common.noticeTitle,
        message: STRINGS.auth.changePassword.successMessage,
        buttons: [{ text: STRINGS.common.buttons.dismiss, onPress: () => signOut() }],
      });
    } catch (e) {
      if (shouldSuppressErrorAlert(e)) {
        return;
      }

      const err = e as { code?: string; message?: string };
      const code = err?.code ?? '';

      const message =
        code === 'auth/wrong-password' || code === 'auth/invalid-credential'
          ? STRINGS.auth.enterPassword.errorCurrentPassword
          : STRINGS.errors.firestoreTimeout;

      showAlert({
        title: STRINGS.common.noticeTitle,
        message,
        buttons: [{ text: STRINGS.common.buttons.dismiss }],
      });
    } finally {
      hideGlobalLoading();
    }
  }, [
    confirmPassword,
    currentPassword,
    hideGlobalLoading,
    newPassword,
    showGlobalLoading,
    user?.email,
    user?.uid,
  ]);

  const onChangeCurrentPassword = useCallback((text: string) => {
    setCurrentPassword(text);
    setFieldErrors(prev => (prev.currentPassword ? { ...prev, currentPassword: undefined } : prev));
  }, []);

  const onChangeNewPassword = useCallback(
    (text: string) => {
      setNewPassword(text);
      if (confirmMismatchError) setConfirmMismatchError(null);
      setFieldErrors(prev => (prev.newPassword ? { ...prev, newPassword: undefined } : prev));
    },
    [confirmMismatchError],
  );

  const onChangeConfirmPassword = useCallback(
    (text: string) => {
      setConfirmPassword(text);
      if (confirmMismatchError) setConfirmMismatchError(null);
      setFieldErrors(prev =>
        prev.confirmPassword ? { ...prev, confirmPassword: undefined } : prev,
      );
    },
    [confirmMismatchError],
  );

  return {
    currentPassword,
    newPassword,
    confirmPassword,
    confirmMismatchError,
    fieldErrors,
    onChangeCurrentPassword,
    onChangeNewPassword,
    onChangeConfirmPassword,
    handleCancel,
    handleUpdatePassword,
  };
}
